import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ExitInterviewModule = ({ departure, onClose }) => {
  const [responses, setResponses] = useState({
    reasonForLeaving: '',
    jobSatisfaction: '',
    managementSupport: '',
    workEnvironment: '',
    careerDevelopment: '',
    compensationBenefits: '',
    workLifeBalance: '',
    wouldRecommend: '',
    wouldReturn: '',
    additionalComments: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const satisfactionOptions = [
    { value: '', label: 'Select rating' },
    { value: '5', label: 'Very Satisfied' },
    { value: '4', label: 'Satisfied' },
    { value: '3', label: 'Neutral' },
    { value: '2', label: 'Dissatisfied' },
    { value: '1', label: 'Very Dissatisfied' }
  ];

  const yesNoOptions = [
    { value: '', label: 'Select option' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'maybe', label: 'Maybe' }
  ];

  const reasonOptions = [
    { value: '', label: 'Select reason' },
    { value: 'better-opportunity', label: 'Better Career Opportunity' },
    { value: 'relocation', label: 'Relocation' },
    { value: 'compensation', label: 'Compensation/Benefits' },
    { value: 'work-life-balance', label: 'Work-Life Balance' },
    { value: 'management', label: 'Management Issues' },
    { value: 'career-growth', label: 'Limited Career Growth' },
    { value: 'personal', label: 'Personal Reasons' },
    { value: 'retirement', label: 'Retirement' },
    { value: 'other', label: 'Other' }
  ];

  const handleSelectChange = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!responses?.reasonForLeaving) {
      alert('Please select a reason for leaving');
      return;
    }

    console.log('Exit Interview Responses:', responses);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-4 rounded-full">
              <Icon name="CheckCircle" size={48} className="text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Exit Interview Completed
          </h2>
          <p className="text-gray-600 mb-6">
            Thank you for your valuable feedback. Your responses will help us improve our workplace.
          </p>
          <Button onClick={onClose} className="bg-gradient-to-r from-amber-600 to-amber-700 text-white">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Icon name="MessageSquare" size={24} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Exit Interview</h2>
            <p className="text-sm text-gray-600">{departure?.employeeName}</p>
          </div>
        </div>
        <Button onClick={onClose} variant="outline" size="sm">
          <Icon name="X" size={16} className="mr-1" />
          Close
        </Button>
      </div>

      {/* Interview Form */}
      <div className="space-y-6">
        {/* Reason for Leaving */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Primary Reason for Leaving *
          </label>
          <Select
            value={responses?.reasonForLeaving}
            onChange={(e) => handleSelectChange('reasonForLeaving', e?.target?.value)}
            options={reasonOptions}
          />
        </div>

        {/* Job Satisfaction */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Overall Job Satisfaction
          </label>
          <Select
            value={responses?.jobSatisfaction}
            onChange={(e) => handleSelectChange('jobSatisfaction', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Management Support */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Management Support
          </label>
          <Select
            value={responses?.managementSupport}
            onChange={(e) => handleSelectChange('managementSupport', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Work Environment */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Work Environment
          </label>
          <Select
            value={responses?.workEnvironment}
            onChange={(e) => handleSelectChange('workEnvironment', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Career Development */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Career Development Opportunities
          </label>
          <Select
            value={responses?.careerDevelopment}
            onChange={(e) => handleSelectChange('careerDevelopment', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Compensation & Benefits */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Compensation & Benefits
          </label>
          <Select
            value={responses?.compensationBenefits}
            onChange={(e) => handleSelectChange('compensationBenefits', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Work-Life Balance */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Work-Life Balance
          </label>
          <Select
            value={responses?.workLifeBalance}
            onChange={(e) => handleSelectChange('workLifeBalance', e?.target?.value)}
            options={satisfactionOptions}
          />
        </div>

        {/* Would Recommend */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Would you recommend this organization to others?
          </label>
          <Select
            value={responses?.wouldRecommend}
            onChange={(e) => handleSelectChange('wouldRecommend', e?.target?.value)}
            options={yesNoOptions}
          />
        </div>

        {/* Would Return */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Would you consider returning to this organization in the future?
          </label>
          <Select
            value={responses?.wouldReturn}
            onChange={(e) => handleSelectChange('wouldReturn', e?.target?.value)}
            options={yesNoOptions}
          />
        </div>

        {/* Additional Comments */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Additional Comments or Suggestions
          </label>
          <textarea
            value={responses?.additionalComments}
            onChange={(e) => handleSelectChange('additionalComments', e?.target?.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            placeholder="Share any additional feedback or suggestions..."
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            onClick={handleSubmit}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white"
          >
            <Icon name="Send" size={20} className="mr-2" />
            Submit Exit Interview
          </Button>
        </div>
      </div>

      {/* Privacy Note */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Lock" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">
              Confidential Feedback
            </p>
            <p className="text-xs text-blue-700">
              Your responses will be kept confidential and used solely for organizational improvement purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitInterviewModule;